import React, { useState, useRef } from "react";
import { InfoCircle, Trash } from "iconsax-react";
import { enqueueSnackbar } from "notistack";

interface AttachmentFieldProps {
  label: string;
  onChange: (file: File | null) => void;
  error?: boolean;
  required?: boolean;
  success?: boolean;
}

const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
];

const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const AttachmentField: React.FC<AttachmentFieldProps> = ({ label, onChange, error = false, success = false, required = false }) => {
  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (selectedFile: File): { valid: boolean; message?: string } => {
    if (selectedFile.size > MAX_FILE_SIZE) {
      return { valid: false, message: `File size must be less than 10MB. Your file is ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB` };
    }

    const fileExtension = "." + selectedFile.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return { valid: false, message: `File type not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}` };
    }

    if (!ALLOWED_MIME_TYPES.includes(selectedFile.type)) {
      return { valid: false, message: `MIME type not allowed. File: ${selectedFile.type}` };
    }

    return { valid: true };
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (selectedFile) {
      const validation = validateFile(selectedFile);
      if (!validation.valid) {
        setErrorMessage(validation.message || "Invalid file");
        enqueueSnackbar(validation.message || "Invalid file", { variant: "error" });
        setFile(null);
        onChange(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setErrorMessage("");
    }

    setFile(selectedFile);
    onChange(selectedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setErrorMessage("");
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getInputClasses = () => {
    let classes = "w-fit grid place-content-center px-3 py-2 text-xs transition-colors duration-600 bg-[#F9F9FA] border rounded-lg outline-none min-h-14";
    if (error || errorMessage) classes += " border-english-vermillion-300";
    return classes;
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-2 text-xs text-closed-shutter-300">
        {label}
        {required && <span className="text-english-vermillion-300">*</span>}
      </label>
      <div className="flex flex-col gap-2">
        <div className="flex items-center">
          <div className={getInputClasses()}>
            <label className="flex items-center justify-center w-full h-full gap-2 cursor-pointer text-closed-shutter-200">
              <input type="file" className="hidden" onChange={handleFileChange} ref={fileInputRef} accept={ALLOWED_EXTENSIONS.join(",")} />
              <InfoCircle className="size-5" variant="Broken" />
              <span className="text-xs">{file ? "Change File" : "Select File"}</span>
            </label>
          </div>
          {file ? (
            <div className="flex items-center gap-3 p-2">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-closed-shutter-300 truncate max-w-[200px]">{file.name}</span>
                <span className="text-xs text-closed-shutter-200">{(file.size / 1024).toFixed(2)} KB</span>
              </div>

              <div className="flex gap-2">
                <button onClick={handleRemoveFile} className="transition-colors text-closed-shutter-300 hover:text-english-vermillion-300 duration-600">
                  <Trash className="size-5" variant="Broken" />
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 p-2 rounded-md bg-english-vermillion-100">
            <InfoCircle className="size-4 text-english-vermillion-300 flex-shrink-0 mt-0.5" variant="Broken" />
            <span className="text-xs text-english-vermillion-300">{errorMessage}</span>
          </div>
        )}

        <div className="space-y-1 text-xs text-closed-shutter-200">
          {/* <p>Allowed formats: {ALLOWED_EXTENSIONS.join(", ")}</p> */}
          <p>Upload images, documents, or spreadsheets.</p>
          <p>Max file size: 10 MB</p>
        </div>
      </div>
    </div>
  );
};

export default AttachmentField;
